import { useSyncExternalStore } from 'react'
import type { RoadRecord, RoadsMeta } from './types'

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? '') as string
const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY ?? '') as string

const SHEET_CSV_URL =
  'https://docs.google.com/spreadsheets/d/1l154s8MsRK5YSW0-u7_WyMkQ5WUBhGPj5q9c0DQnFUk/gviz/tq?tqx=out:csv&gid=0'

export interface RoadsResponse {
  roads: RoadRecord[]
  meta: RoadsMeta
}

export async function fetchRoads(): Promise<RoadsResponse> {
  if (SUPABASE_URL && SUPABASE_ANON_KEY) {
    // Don't block on the server: if the roads service hasn't answered within
    // 500ms, start the Google Sheet fallback in parallel and use whichever
    // usable data arrives first.
    const edgePromise = fetchRoadsFromEdge().catch(() => null)
    const timeout = new Promise<null>((resolve) => {
      const id = setTimeout(() => resolve(null), 500)
      if (typeof id === 'object' && id && 'unref' in id) (id as { unref(): void }).unref()
    })
    const first = await Promise.race([edgePromise, timeout])
    if (first) {
      const clean = sanitizeRoadsResponse(first)
      if (clean) return clean
    }

    const sheetPromise = fetchRoadsFromSheet()
    let sheetError: unknown = null
    sheetPromise.catch((err) => {
      sheetError = err
    })
    const winner = await Promise.race([
      edgePromise,
      sheetPromise.then((r) => sanitizeRoadsResponse(r) as RoadsResponse | null).catch(() => null),
    ])
    if (winner) return winner

    // Both paths exhausted: prefer a late edge result, else surface the sheet error.
    const edge = await edgePromise
    if (edge) return edge
    throw sheetError instanceof Error ? sheetError : new Error('Failed to load roads')
  }
  const sheet = await fetchRoadsFromSheet()
  return sanitizeRoadsResponse(sheet) ?? sheet
}

// ── Geometry sanitation ──────────────────────────────────────────────────────
// Runtime data (edge snapshots, sheet fallback) may carry malformed geometry.
// Consumers (map, fit-bounds, rides) trust the type, so normalize HERE once:
// every road ends up with either a clean [lng,lat][] LineString, a flat
// [lng,lat] Point, or geometry: null.
function toLatLngPair(v: unknown): [number, number] | null {
  if (!Array.isArray(v) || v.length < 2) return null
  const lng = v[0]
  const lat = v[1]
  if (typeof lng !== 'number' || typeof lat !== 'number') return null
  if (!isFinite(lng) || !isFinite(lat)) return null
  return [lng, lat]
}

function sanitizeGeometry(raw: unknown): RoadRecord['geometry'] {
  if (!raw || typeof raw !== 'object') return null
  const g = raw as { type?: unknown; coordinates?: unknown }
  if (g.type === 'LineString') {
    if (!Array.isArray(g.coordinates)) return null
    const coords: [number, number][] = []
    for (const c of g.coordinates) {
      const pair = toLatLngPair(c)
      if (pair) coords.push(pair)
    }
    if (coords.length < 2) return null
    return { type: 'LineString', coordinates: coords }
  }
  if (g.type === 'Point') {
    // Accept both a flat [lng,lat] and a nested [[lng,lat]].
    const pair = toLatLngPair(g.coordinates) ?? toLatLngPair((g.coordinates as unknown[])?.[0])
    return pair ? { type: 'Point', coordinates: pair } : null
  }
  return null
}

export function sanitizeRoadsResponse(res: RoadsResponse | null): RoadsResponse | null {
  if (!res || !Array.isArray(res.roads)) return null
  const roads = res.roads.filter(Boolean).map((r) => ({ ...r, geometry: sanitizeGeometry(r.geometry) }))
  return { roads, meta: res.meta }
}

async function fetchRoadsFromEdge(): Promise<RoadsResponse> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/roads`, {
    headers: { Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
  })
  if (!res.ok) throw new Error(`roads service failed (${res.status})`)
  return (await res.json()) as RoadsResponse
}

// Lightweight freshness probe: asks the roads backend for its committed
// metadata only (no roads payload). Returns the committed content signature
// and the database bigint revision as a decimal string — either may be null
// (backend without the action=meta path, cold start, offline…). Callers
// treat null as "unknown" and fall back to a full refresh — never to
// skipping one. The revision is compared AS A STRING, never via Number().
export interface RoadsVersionMeta {
  version_sig: string | null
  sync_revision: string | null
}

export async function fetchRoadsVersionMeta(): Promise<RoadsVersionMeta> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return { version_sig: null, sync_revision: null }
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/roads?action=meta`, {
      headers: { Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
    })
    if (!res.ok) return { version_sig: null, sync_revision: null }
    const body = (await res.json()) as {
      meta?: { version_sig?: string; sync_revision?: string } | null
    }
    const sig = body.meta?.version_sig
    const rev = body.meta?.sync_revision
    return {
      version_sig: typeof sig === 'string' && sig.length > 0 ? sig : null,
      sync_revision: typeof rev === 'string' && rev.length > 0 ? rev : null,
    }
  } catch {
    return { version_sig: null, sync_revision: null }
  }
}

export function getRegions(roads: RoadRecord[]): string[] {
  return [...new Set((roads ?? []).map((r) => r.region).filter(Boolean))].sort()
}

function parseCsvText(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i++
        } else inQuotes = false
      } else field += ch
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      row.push(field)
      field = ''
    } else if (ch === '\n') {
      row.push(field)
      rows.push(row)
      row = []
      field = ''
    } else if (ch !== '\r') {
      field += ch
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field)
    rows.push(row)
  }
  return rows
}

function norm(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim()
}

function normalizeBikeType(tok: string): string {
  const t = tok.trim().toLowerCase()
  if (t === 'supermoto') return 'Sumo'
  if (t === 'adv' || t === 'adventure' || t === 'dual sport') return 'ADV'
  if (t === 'sport') return 'Sport'
  if (t === 'cruiser') return 'Cruiser'
  if (t === 'sumo') return 'Sumo'
  return tok.trim()
}

function parseRatingValue(v: string | undefined): number | null {
  if (!v) return null
  const n = parseFloat(v)
  if (isNaN(n)) return null
  return Math.min(10, Math.max(0, n))
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Verbatim mirror of extractOrderedWaypoints() in
// backend/supabase/functions/_shared/roadsSync.ts — the Vite dev server cannot
// resolve files outside frontend/, so the fallback keeps its own copy. The
// /dir/ path keeps URL order (= true travel order); blob pins (!1d..!2d /
// !3d..!4d) have UNKNOWN position, so nearest-insertion places each at the
// chain position minimizing straight-line detour. If the backend version
// changes, update this copy to match.
const MAP_COORD_RE =
  /(?<!@)(-?\d{1,2}\.\d+),(-?\d{1,3}\.\d+)|!1d(-?\d{1,3}\.\d+)!2d(-?\d{1,2}\.\d+)|!3d(-?\d{1,2}\.\d+)!4d(-?\d{1,3}\.\d+)/g

function haversine([lng1, lat1]: number[], [lng2, lat2]: number[]): number {
  const R = 6371000
  const toRad = Math.PI / 180
  const dLat = (lat2 - lat1) * toRad
  const dLng = (lng2 - lng1) * toRad
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export function extractLinkWaypoints(rawLink: string): [number, number][] {
  const path: [number, number][] = []
  const blob: [number, number][] = []
  const seen = new Set<string>()
  const push = (list: [number, number][], lat: number, lng: number) => {
    if (lat < 20 || lat > 50 || lng < -130 || lng > -60) return
    const key = `${lat.toFixed(5)},${lng.toFixed(5)}`
    if (seen.has(key)) return
    seen.add(key)
    list.push([lng, lat])
  }
  let m: RegExpExecArray | null
  MAP_COORD_RE.lastIndex = 0
  while ((m = MAP_COORD_RE.exec(rawLink)) !== null) {
    if (m[1] !== undefined) push(path, parseFloat(m[1]), parseFloat(m[2]))
    else if (m[3] !== undefined) push(blob, parseFloat(m[4]), parseFloat(m[3]))
    else if (m[5] !== undefined) push(blob, parseFloat(m[5]), parseFloat(m[6]))
  }
  if (path.length === 0) return blob
  if (blob.length === 0) return path
  const chain = [...path]
  for (const pt of blob) {
    let bestIdx = chain.length
    let bestCost = Infinity
    for (let i = 0; i <= chain.length; i++) {
      const cost =
        (i > 0 ? haversine(chain[i - 1], pt) : 0) +
        (i < chain.length ? haversine(pt, chain[i]) : 0) -
        (i > 0 && i < chain.length ? haversine(chain[i - 1], chain[i]) : 0)
      if (cost < bestCost) {
        bestCost = cost
        bestIdx = i
      }
    }
    chain.splice(bestIdx, 0, pt)
  }
  return chain
}

export function parseSheetRoads(csv: string): {
  roads: RoadRecord[]
  rowCount: number
  skipped: number
} {
  const rows = parseCsvText(csv)
  const headerIdx = rows.findIndex((r) => r.some((c) => norm(c ?? '') === 'region'))
  if (headerIdx === -1) throw new Error('Header row not found in CSV')
  const headers = rows[headerIdx].map(norm)
  const data = rows.slice(headerIdx + 1)

  const findIdx = (test: (h: string) => boolean) => headers.findIndex(test)
  const iRegion = findIdx((h) => h === 'region')
  const iGroup = findIdx((h) => h === 'group')
  const iName = findIdx((h) => h.includes('road name'))
  let iDist = findIdx((h) => h.includes('distance') && h.includes('mile'))
  if (iDist === -1 && iName >= 0) iDist = iName + 1
  const iPaved = findIdx((h) => h.includes('fully paved'))
  const iBikes = findIdx((h) => h.includes('good bike types'))
  const iNotes = findIdx((h) => h === 'notes')
  const iLink = findIdx((h) => h.includes('raw link'))
  const iUuid = findIdx((h) => h.includes('uuid'))

  let iOverall = iBikes + 1
  let iFun = iBikes + 2
  let iPavement = iBikes + 3
  let iTraffic = iBikes + 4
  let iDirt = iBikes + 5
  if (iBikes >= 0 && !headers[iOverall]?.includes('overall')) {
    const kOverall = findIdx((h) => h.includes('overall'))
    if (kOverall >= 0) {
      iOverall = kOverall
      iFun = findIdx((h) => h.includes('fun'))
      iPavement = findIdx((h) => h.includes('pavement'))
      iTraffic = findIdx((h) => h.includes('traffic'))
      iDirt = findIdx((h) => h.includes('dirt'))
    }
  }

  const seenIds = new Map<string, number>()
  const roads: RoadRecord[] = []
  let skipped = 0

  for (const r of data) {
    const region = (r[iRegion] ?? '').trim()
    const name = (r[iName] ?? '').trim()
    if (!region || !name) {
      skipped++
      continue
    }
    const rawLink = (r[iLink] ?? '').trim()
    const googleMapsUrl =
      rawLink.startsWith('http') && rawLink.includes('google.com/maps') ? rawLink : null
    const coords = googleMapsUrl ? extractLinkWaypoints(googleMapsUrl) : []
    const uuid = iUuid >= 0 ? (r[iUuid] ?? '').trim() : ''
    let id = UUID_RE.test(uuid) ? uuid.toLowerCase() : `sheet-${headerIdx + 1 + roads.length + skipped}`
    const count = seenIds.get(id) ?? 0
    seenIds.set(id, count + 1)
    if (count > 0) id = `${id}-${count}`

    let geometry: RoadGeometry | null = null
    if (coords.length >= 2) {
      geometry = { type: 'LineString', coordinates: coords }
    } else if (coords.length === 1) {
      geometry = { type: 'Point', coordinates: coords[0] }
    }

    roads.push({
      id,
      region,
      group: (r[iGroup] ?? '').trim(),
      name,
      distance_miles:
        iDist >= 0 && !isNaN(parseFloat(r[iDist])) ? parseFloat(r[iDist]) : null,
      fully_paved: (() => {
        if (iPaved < 0) return null
        const v = (r[iPaved] ?? '').trim().toUpperCase()
        if (!v) return null
        return v.startsWith('Y')
      })(),
      bike_types:
        iBikes >= 0 ? (r[iBikes] ?? '').split(',').map(normalizeBikeType).filter(Boolean) : [],
      ratings: {
        overall: parseRatingValue(r[iOverall]),
        fun: parseRatingValue(r[iFun]),
        pavement: parseRatingValue(r[iPavement]),
        traffic: parseRatingValue(r[iTraffic]),
        dirt: parseRatingValue(r[iDirt]),
      },
      description: iNotes >= 0 ? (r[iNotes] ?? '').trim() : '',
      google_maps_url: googleMapsUrl,
      geometry,
    })
  }

  return { roads, rowCount: data.length, skipped }
}

async function fetchRoadsFromSheet(): Promise<RoadsResponse> {
  const res = await fetch(SHEET_CSV_URL)
  if (!res.ok) throw new Error(`Google Sheet fetch failed (${res.status})`)
  const csv = await res.text()
  const parsed = parseSheetRoads(csv)
  if (parsed.roads.length === 0) throw new Error('No valid roads found in sheet')
  const regions = [...new Set(parsed.roads.map((r) => r.region))].sort()
  return {
    roads: parsed.roads,
    meta: {
      fetched_at: new Date().toISOString(),
      row_count: parsed.rowCount,
      valid_count: parsed.roads.length,
      skipped_count: parsed.skipped,
      regions,
      warning: 'Served from client-side Google Sheet fallback',
    },
  }
}

// ── Geometry resolution ──────────────────────────────────────────────────────
// Google Maps links carry only 2-6 coarse waypoints. The road-geometry service
// map-matches them onto the real road network (OSRM) and caches the dense
// polyline in the database, so each road is snapped exactly once per link.
// Batches of roads are resolved per request; per-session results are memoized.
const geometryCache = new Map<string, [number, number][] | null>()
// The Google Maps link each cached geometry was derived from — a changed link
// invalidates the cache entry.
const cachedLink = new Map<string, string | null>()
// Geometry batches resolve asynchronously; this map no longer feeds display
// values (estimates are distance/speed) but still drives the version counter
// that re-renders panels once a road's snapped geometry is available.
const durations = new Map<string, number | null>()

// ─── Persistent geometry cache (IndexedDB) ──────────────────────────────────
// The in-memory session cache dies on reload; the browser DB survives it, so
// repeat visits skip the geometry service entirely for unchanged links.
// Best-effort: any failure (private mode, quota) just means no persistence.
interface StoredGeometry {
  link: string | null
  coords: [number, number][]
  duration: number | null
  elevation?: (number | null)[] | null
  // Set once this entry has been confirmed current against the server by a
  // freshness check (or written fresh from the service). Unmarked entries are
  // legacy/pre-elevation cache rows — they get one lightweight check and are
  // then either refreshed or marked.
  checked?: boolean
}

// Per-road elevation profile (meters, 1:1 with the cached polyline). Fetched
// server-side during geometry generation; served from the same caches.
const elevations = new Map<string, (number | null)[]>()
let elevationVersion = 0
const elevationListeners = new Set<() => void>()
function bumpElevationVersion() {
  elevationVersion++
  for (const cb of elevationListeners) cb()
}
export function useElevationVersion(): number {
  return useSyncExternalStore(
    (cb) => {
      elevationListeners.add(cb)
      return () => {
        elevationListeners.delete(cb)
      }
    },
    () => elevationVersion,
  )
}

// Elevation profile for a road (aligned with its cached polyline), or null
// while it hasn't arrived / the road has none.
export function roadElevation(road: RoadRecord): (number | null)[] | null {
  const link = road.google_maps_url
  if (link) {
    const direct = elevations.get(link)
    if (direct) return direct
  }
  return null
}
const GEOM_DB = 'roadratings-geometry'
const GEOM_STORE = 'geometry'
let geomDbPromise: Promise<IDBDatabase | null> | null = null

function openGeomDb(): Promise<IDBDatabase | null> {
  if (!geomDbPromise) {
    geomDbPromise = new Promise((resolve) => {
      try {
        if (typeof indexedDB === 'undefined') {
          resolve(null)
          return
        }
        const req = indexedDB.open(GEOM_DB, 1)
        req.onupgradeneeded = () => {
          if (!req.result.objectStoreNames.contains(GEOM_STORE)) {
            req.result.createObjectStore(GEOM_STORE)
          }
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => resolve(null)
        req.onblocked = () => resolve(null)
      } catch {
        resolve(null)
      }
    })
  }
  return geomDbPromise
}

function geomIdbGet(roadId: string): Promise<StoredGeometry | null> {
  return openGeomDb().then(
    (db) =>
      new Promise<StoredGeometry | null>((resolve) => {
        if (!db) {
          resolve(null)
          return
        }
        try {
          const tx = db.transaction(GEOM_STORE, 'readonly')
          const req = tx.objectStore(GEOM_STORE).get(roadId)
          req.onsuccess = () => resolve((req.result as StoredGeometry | undefined) ?? null)
          req.onerror = () => resolve(null)
        } catch {
          resolve(null)
        }
      }),
  )
}

function geomIdbPut(
  roadId: string,
  link: string | null,
  coords: [number, number][],
  duration: number | null,
  elevation?: (number | null)[] | null,
  checked = true,
) {
  void openGeomDb().then((db) => {
    if (!db) return
    try {
      const tx = db.transaction(GEOM_STORE, 'readwrite')
      tx.objectStore(GEOM_STORE).put(
        { link, coords, duration, elevation: elevation ?? null, checked } satisfies StoredGeometry,
        roadId,
      )
    } catch {
      // Quota or availability issue — persistence stays best-effort.
    }
  })
}

// ─── Freshness check queue ────────────────────────────────────────────────
// A locally cached road is only as good as its last sync with the server —
// entries written before a server-side improvement (elevation profiles,
// corrected polylines) would otherwise be served forever. The browser asks
// the geometry service about its STORED roads in small batches ({ id, link }
// pairs only — a few dozen bytes per road); the server answers with ONLY the
// ids that need an update, and those roads are re-fetched through the normal
// pipeline (served from the server's own cache, so no OSRM/elevation work).
// One check per road per stored entry: refreshed or confirmed entries are
// marked `checked` and never re-probed.
let checkTimer: ReturnType<typeof setTimeout> | null = null
const checkQueue = new Map<string, { id: string; link: string | null }>()

export function queueGeometryFreshnessCheck(roadId: string, link: string | null) {
  checkQueue.set(roadId, { id: roadId, link })
  if (checkTimer !== null) return
  checkTimer = setTimeout(() => {
    checkTimer = null
    const batch = [...checkQueue.values()].slice(0, 60)
    for (const item of batch) checkQueue.delete(item.id)
    if (batch.length === 0) return
    void runFreshnessCheck(batch)
  }, 1500)
}

async function runFreshnessCheck(
  batch: { id: string; link: string | null }[],
): Promise<void> {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/road-geometry`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        check: batch.map((e) => ({ id: e.id, link: e.link ?? '' })),
      }),
    })
    if (!res.ok) return
    const json = (await res.json()) as { updates?: Record<string, boolean> }
    const updates = json.updates ?? {}
    const refreshRoads: RoadRecord[] = []
    for (const item of batch) {
      if (updates[item.id] !== true) {
        // Server says current: mark the stored entry so it is never probed again.
        const stored = await geomIdbGet(item.id)
        if (stored) geomIdbPut(item.id, stored.link, stored.coords, stored.duration, stored.elevation, true)
        continue
      }
      // Needs update: clear the local entry and re-resolve through the normal
      // pipeline. A RoadRecord is enough — resolveDetailedGeometry reads the
      // link, region and mileage it needs from it.
      await geomIdbDelete(item.id)
      geometryCache.delete(item.id)
      cachedLink.delete(item.id)
      const link = item.link ?? ''
      if (link) {
        refreshRoads.push({
          id: item.id,
          region: '',
          group: '',
          name: '',
          distance_miles: null,
          fully_paved: null,
          bike_types: [],
          ratings: { overall: null, fun: null, pavement: null, traffic: null, dirt: null },
          description: '',
          google_maps_url: link,
          geometry: { type: 'LineString', coordinates: [[0, 0], [0, 0]] },
        })
      }
    }
    for (const road of refreshRoads) {
      try {
        await resolveDetailedGeometry(road)
      } catch {
        // Re-fetch is best-effort; the raw-waypoint fallback still renders.
      }
    }
    if (refreshRoads.length > 0) bumpElevationVersion()
  } catch {
    // Network or service unavailable — entries stay unmarked and will be
    // re-probed on a future visit. Never a correctness risk.
  }
}

function geomIdbDelete(roadId: string) {
  void openGeomDb().then((db) => {
    if (!db) return
    try {
      const tx = db.transaction(GEOM_STORE, 'readwrite')
      tx.objectStore(GEOM_STORE).delete(roadId)
    } catch {
      // best-effort
    }
  })
}

// Max roads per geometry request — one full batch per round-trip instead of
// ~6-road dribbles.
export const MAX_GEOMETRY_BATCH = 40

// Durations arrive asynchronously (they land together with the road's snapped
// geometry), so panels subscribe to this version counter: every resolved batch
// bumps it and re-renders everything showing travel times.
let durationVersion = 0
const durationListeners = new Set<() => void>()
export function useDurationsVersion(): number {
  return useSyncExternalStore(
    (cb) => {
      durationListeners.add(cb)
      return () => {
        durationListeners.delete(cb)
      }
    },
    () => durationVersion,
  )
}

// Travel time starts from the routing service's estimate (persisted per link
// during geometry generation). That estimate assumes car speed limits, which
// on rural twisty roads can imply averages below 10 mph — 3-4x slower than
// real riding. When the implied average drops under MIN_PLAUSIBLE_MPH the
// estimate is rejected and replaced with distance ÷ CORRECTED_MPH, and
// isRoughEstimate flags the panel to show a caveat notice.
export const MIN_PLAUSIBLE_MPH = 20
// Last-resort speed when no curvature model can be built yet (e.g. no
// calibrated roads available this session).
export const CORRECTED_MPH = 45

// ─── Curvature-based fallback speed model ─────────────────────────────────
// The routing service's durations assume car speed limits, which on rural
// twisty roads can imply averages below 10 mph — 3-4x slower than real
// riding. Rather than correcting every rejected estimate with one flat
// speed, we calibrate speed against road curvature using roads whose service
// estimate WAS accepted (implied average >= MIN_PLAUSIBLE_MPH): each such
// road contributes a (curvature, speed) sample, and a linear model
// speed = intercept + slope * curvature is fit over them. Roads that need a
// fallback then get an estimate scaled by how twisty their polyline is.
interface SpeedModel {
  intercept: number
  slope: number
}
interface CalibrationSample {
  link: string
  coords: [number, number][]
}
const calibrationSamples = new Map<string, CalibrationSample>()
// Curvature per road is expensive to measure (full polyline walk), so it's
// computed once and memoized — the model rebuild itself must stay cheap or it
// blocks the main thread during geometry bursts.
const curvatureCache = new Map<string, number>()
let speedModelSig = ''
let speedModel: SpeedModel | null = null

function roadCurvature(id: string, coords: [number, number][]): number {
  let c = curvatureCache.get(id)
  if (c === undefined) {
    c = curvatureDegPerMile(coords)
    curvatureCache.set(id, c)
  }
  return c
}

function bearingDeg(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const y =
    Math.sin(toRad(lon2 - lon1)) * Math.cos(toRad(lat2))
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lon2 - lon1))
  return (Math.atan2(y, x) * 180) / Math.PI
}

// Total bearing change per mile along a polyline. Tiny segments (< 20 m) are
// skipped so rendering jitter doesn't read as cornering.
function curvatureDegPerMile(coords: [number, number][]): number {
  if (coords.length < 3) return 0
  const R = 6371 // km
  const toRad = (d: number) => (d * Math.PI) / 180
  const segs: { lenKm: number; brg: number }[] = []
  let miles = 0
  for (let i = 1; i < coords.length; i++) {
    const [lon1, lat1] = coords[i - 1]
    const [lon2, lat2] = coords[i]
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
    const km = 2 * R * Math.asin(Math.sqrt(Math.min(1, a)))
    segs.push({ lenKm: km, brg: bearingDeg(lon1, lat1, lon2, lat2) })
    miles += km / 1.609344
  }
  if (miles < 0.2) return 0
  let totalTurn = 0
  for (let i = 1; i < segs.length; i++) {
    if (segs[i - 1].lenKm < 0.02 || segs[i].lenKm < 0.02) continue
    let d = Math.abs(segs[i].brg - segs[i - 1].brg)
    if (d > 180) d = 360 - d
    totalTurn += d
  }
  return totalTurn / miles
}

function computeSpeedModel(): SpeedModel | null {
  const xs: number[] = []
  const ys: number[] = []
  for (const [roadId, entry] of calibrationSamples) {
    const dur = durations.get(entry.link)
    if (dur == null || dur <= 0) continue
    const miles = polylineMiles(entry.coords)
    if (miles < 0.3) continue
    const mph = (miles / dur) * 3600
    // Only roads with a trusted service estimate train the model.
    if (mph < MIN_PLAUSIBLE_MPH || mph > 70) continue
    xs.push(roadCurvature(roadId, entry.coords))
    ys.push(mph)
  }
  const n = xs.length
  if (n < 3) return null
  if (n < 5) return null
  const mx = xs.reduce((a, b) => a + b, 0) / n
  const my = ys.reduce((a, b) => a + b, 0) / n
  let sxx = 0
  let sxy = 0
  for (let i = 0; i < n; i++) {
    sxx += (xs[i] - mx) ** 2
    sxy += (xs[i] - mx) * (ys[i] - my)
  }
  if (sxx < 1e-6) return null
  const slope = sxy / sxx
  // No usable inverse curvature-speed relationship → the flat correction is
  // the safer fallback.
  if (!isFinite(slope) || slope >= 0) return null
  return { intercept: my - slope * mx, slope }
}

function getSpeedModel(): SpeedModel | null {
  const sig = `${durationVersion}:${calibrationSamples.size}`
  if (speedModelSig === sig) return speedModel
  speedModelSig = sig
  speedModel = computeSpeedModel()
  return speedModel
}

// Fallback speed for a road, derived from its polyline curvature via the
// calibrated model. Null when there's no usable geometry or model yet.
function curvatureEstimateMph(road: RoadRecord): number | null {
  const entry = calibrationSamples.get(road.id)
  if (!entry) return null
  const model = getSpeedModel()
  if (!model) return null
  const c = roadCurvature(road.id, entry.coords)
  const mph = model.intercept + model.slope * c
  return Math.min(60, Math.max(12, mph))
}

export function roadDurationSeconds(road: RoadRecord): number | null {
  if (!road) return null
  const link = road.google_maps_url
  const service = link ? durations.get(link) : null
  let miles: number | null = null
  if (typeof road.distance_miles === 'number' && road.distance_miles > 0) {
    miles = road.distance_miles
  } else {
    const geom = geometryCache.get(road.id)
    if (geom && geom.length > 1) {
      miles = polylineMiles(geom)
    }
  }

  if (service != null && service > 0) {
    const serviceMph = miles != null ? (miles / service) * 3600 : Infinity
    if (serviceMph >= MIN_PLAUSIBLE_MPH) return service
  }

  if (miles == null) return null
  const mph = curvatureEstimateMph(road) ?? CORRECTED_MPH
  return (miles / mph) * 3600
}

// ─── Trim-aware mileage/time ────────────────────────────────────────────────
// A trim's cuts are stored as fractions (0..1) along the road's length, so the
// kept portion between them scales both mileage and travel time — letting the
// panels describe the ridden section instead of the full road.

export function trimKeptFraction(
  trim?: { head?: { pos: number } | null; tail?: { pos: number } | null } | null,
): number {
  if (!trim) return 1
  const start = typeof trim.head?.pos === 'number' ? trim.head.pos : 0
  const end = typeof trim.tail?.pos === 'number' ? trim.tail.pos : 1
  return Math.max(0, Math.min(1, end - start))
}

export function trimScaledDurationSeconds(
  road: RoadRecord,
  trim?: { head?: { pos: number } | null; tail?: { pos: number } | null } | null,
): number | null {
  const full = roadDurationSeconds(road)
  if (full == null) return null
  return full * trimKeptFraction(trim)
}

// True when the displayed time came from the ~45 mph correction rather than
// the routing service — panels surface this as a rough-estimate notice.
export function isRoughEstimate(road: RoadRecord): boolean {
  if (!road) return false
  const link = road.google_maps_url
  const service = link ? durations.get(link) : null
  if (service != null && service > 0) {
    let miles: number | null = null
    if (typeof road.distance_miles === 'number' && road.distance_miles > 0) {
      miles = road.distance_miles
    } else {
      const geom = geometryCache.get(road.id)
      if (geom && geom.length > 1) {
        miles = polylineMiles(geom)
      }
    }
    const serviceMph = miles != null ? (miles / service) * 3600 : Infinity
    if (serviceMph >= MIN_PLAUSIBLE_MPH) return false
  }
  // No service time at all, or it was rejected → the correction was applied.
  return roadDurationSeconds(road) != null
}

// Haversine length of a polyline, in miles.
function polylineMiles(coords: [number, number][]): number {
  const R = 6371 // km
  const toRad = (d: number) => (d * Math.PI) / 180
  let km = 0
  for (let i = 1; i < coords.length; i++) {
    const [lon1, lat1] = coords[i - 1]
    const [lon2, lat2] = coords[i]
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
    km += 2 * R * Math.asin(Math.sqrt(a))
  }
  return km / 1.609344
}

export function formatDuration(seconds: number | null): string | null {
  if (seconds == null || !isFinite(seconds) || seconds <= 0) return null
  const mins = Math.round(seconds / 60)
  if (mins < 60) return `${mins} min`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

let batchRef: {
  promise: Promise<Map<string, [number, number][] | null>>
  add: (link: string) => Promise<[number, number][] | null>
} | null = null

function requestBatch(): NonNullable<typeof batchRef> {
  if (batchRef) return batchRef
  const links: string[] = []
  // Road metadata (id/name/sheet mileage) keyed by link, so the geometry
  // service can audit sheet-vs-OSRM mileage for freshly generated polylines.
  const roadMeta = new Map<string, { id: string; name: string; distance_miles: number | null; google_maps_url: string }>()
  const pending = new Map<string, Promise<[number, number][] | null>>()
  let flushTimer: ReturnType<typeof setTimeout> | null = null

  const flush = () => {
    const batch = [...links]
    const meta = [...roadMeta.values()]
    links.length = 0
    roadMeta.clear()
    batchRef = null
    flushTimer = null
    if (batch.length === 0) return
    const promise = (async () => {
      const result = new Map<string, [number, number][] | null>()
      try {
        const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL ?? '') as string
        const supabaseKey = (import.meta.env.VITE_SUPABASE_ANON_KEY ?? '') as string
        if (!supabaseUrl || !supabaseKey) throw new Error('no supabase env')
        const res = await fetch(`${supabaseUrl}/functions/v1/road-geometry`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${supabaseKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ links: batch, roads: meta }),
        })
        if (!res.ok) throw new Error(`road-geometry failed (${res.status})`)
        const json = (await res.json()) as {
          geometry: Record<
            string,
            {
              coordinates: [number, number][]
              duration_s?: number | null
              elevation?: (number | null)[] | null
            } | undefined
          >
        }
        let sawDuration = false
        let sawElevation = false
        for (const link of batch) {
          const entry = json.geometry?.[link]
          const coords = entry?.coordinates
          result.set(link, Array.isArray(coords) && coords.length >= 1 ? coords : null)
          // OSRM-reported travel time at the speed limit, persisted per link
          // alongside the geometry cache by the road-geometry service.
          durations.set(link, typeof entry?.duration_s === 'number' ? entry.duration_s : null)
          if (typeof entry?.duration_s === 'number') sawDuration = true
          // Elevation profile rides along the same response (aligned 1:1 with
          // the polyline); kept keyed by link like durations.
          if (entry?.elevation && Array.isArray(entry.elevation)) {
            elevations.set(link, entry.elevation)
            sawElevation = true
          }
        }
        if (sawDuration) {
          durationVersion++
          for (const cb of durationListeners) cb()
        }
        if (sawElevation) bumpElevationVersion()
      } catch {
        // Service unavailable → null for every link in the batch; the caller
        // falls back to the raw waypoints.
        for (const link of batch) result.set(link, null)
      }
      return result
    })()
    for (const link of batch) {
      pending.get(link)?.(promise.then((r) => r.get(link) ?? null))
    }
    return promise
  }

  const instance = {
    add(
      link: string,
      road?: { id: string; name: string; distance_miles: number | null; region?: string },
    ): Promise<[number, number][] | null> {
      const existing = pending.get(link)
      if (road && link && !roadMeta.has(link)) {
        roadMeta.set(link, { ...road, google_maps_url: link })
      }
      if (existing) return existing
      const promise = new Promise<[number, number][] | null>((resolve) => {
        pending.set(link, resolve)
      })
      links.push(link)
      // Large batches: ship the moment the payload cap is reached so a full
      // collection window always leaves as ONE request; only the trailing
      // remainder of a load waits out the 40ms collection timer.
      if (links.length >= MAX_GEOMETRY_BATCH) {
        if (flushTimer !== null) {
          clearTimeout(flushTimer)
          flushTimer = null
        }
        flush()
        return promise
      }
      if (flushTimer === null) flushTimer = setTimeout(flush, 40)
      return promise
    },
  }
  batchRef = instance
  return instance
}

export async function resolveDetailedGeometry(
  road: RoadRecord
): Promise<[number, number][] | null> {
  if (!road?.geometry) return null
  // A road's geometry is only valid for the Google Maps link it was derived
  // from. If the link changed in the sheet, the cached polyline is stale and
  // must be re-derived.
  const expectedLink = road.google_maps_url ?? null
  const cached = geometryCache.get(road.id)
  if (cached !== undefined && cachedLink.get(road.id) === expectedLink) {
    return cached
  }

  // Persistent cache (IndexedDB): survives page reloads, so repeat visits
  // render roads without hitting the geometry service at all. Entries are
  // keyed to the road's link — a changed sheet link invalidates them.
  const stored = await geomIdbGet(road.id)
  const storedOk =
    !!stored &&
    stored.link === expectedLink &&
    Array.isArray(stored.coords) &&
    stored.coords.length > 0 &&
    stored.coords.every(
      (c) => Array.isArray(c) && typeof c[0] === 'number' && typeof c[1] === 'number',
    )
  if (stored && storedOk) {
    geometryCache.set(road.id, stored.coords)
    cachedLink.set(road.id, expectedLink)
    if (stored.coords.length > 2) {
      calibrationSamples.set(road.id, { link: expectedLink, coords: stored.coords })
      speedModelSig = ''
    }
    if (
      stored.link &&
      typeof stored.duration === 'number' &&
      stored.duration > 0 &&
      !durations.has(stored.link)
    ) {
      durations.set(stored.link, stored.duration)
      durationVersion++
      for (const cb of durationListeners) cb()
    }
    if (stored.link && Array.isArray(stored.elevation) && !elevations.has(stored.link)) {
      elevations.set(stored.link, stored.elevation)
      bumpElevationVersion()
    }
    // Entries written before this freshness system existed carry no `checked`
    // marker. Serve them NOW (zero wait), but ask the server once whether
    // they're still current — an unmarked entry that needs new data (e.g. an
    // elevation profile) is refreshed in the background and marked.
    if (!stored.checked) {
      queueGeometryFreshnessCheck(road.id, stored.link ?? expectedLink)
    }
    return stored.coords
  }

  let coords: [number, number][] | null = null
  let fromService = false
  const link = road.google_maps_url
  if (link && road.geometry.type === 'LineString') {
    coords = await requestBatch().add(link, {
      id: road.id,
      name: road.name,
      distance_miles: road.distance_miles,
      region: road.region,
    })
    if (coords) fromService = true
  }
  if (!coords) {
    // Fallback: the raw extracted waypoints (points render as-is; lines only
    // render once snapped geometry or raw waypoints are available).
    coords =
      road.geometry.type === 'LineString' && Array.isArray(road.geometry.coordinates)
        ? (road.geometry.coordinates as [number, number][])
        : []
  }
  geometryCache.set(road.id, coords)
  cachedLink.set(road.id, road.google_maps_url ?? null)
  // Write-through to the persistent cache — only service-snapped polylines:
  // a failed batch's raw-waypoint fallback must not poison it.
  if (fromService && coords && coords.length > 0) {
    geomIdbPut(
      road.id,
      expectedLink,
      coords,
      link ? durations.get(link) ?? null : null,
      link ? elevations.get(link) ?? null : null,
    )
  }
  // Dense snapped polylines double as curvature samples for the fallback
  // speed model; the service duration (trusted or not) is looked up per road
  // at calibration time.
  if (coords && coords.length > 2) {
    // New geometry arrived (first resolution or the sheet link changed):
    // drop the old curvature so it is re-measured against the new polyline.
    curvatureCache.delete(road.id)
    calibrationSamples.set(road.id, { link: road.google_maps_url ?? '', coords })
    speedModelSig = ''
  }
  return coords
}

// Force-reset support: after a road's record is rebuilt server-side, the
// per-session geometry memo must not keep serving the old polyline.
export function invalidateRoadGeometry(roadId: string) {
  geometryCache.delete(roadId)
  cachedLink.delete(roadId)
  curvatureCache.delete(roadId)
  calibrationSamples.delete(roadId)
  speedModelSig = ''
  geomIdbDelete(roadId)
}
